import { useEffect, useMemo, useState } from 'react';
import { Activity } from 'react';
import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Checkbox from '@ui/checkbox/checkbox';
import Popover from '@ui/popover/popover';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { RegFieldsEnum, RegistrationQuestion } from '@/types/registration.types';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingProjectRegistrationQuestions } from '@/store/slices/project/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { WorkingPasscodeList } from '@/types/passcode-list.types';
import UploadList from '@/connection/audiences/upload-list';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

export type CsvColumnMapping = {
  csvHeader: string;
  fieldId: number;
};

type FieldOption = {
  value: number;
  label: string;
};

type MappingRow = {
  csvHeader: string;
  enabled: boolean;
  fieldId: number | null;
};

type CsvColumnMapperModalProps = {
  open: boolean;
  csvHeaders: string[];
  workingPasscodeList: WorkingPasscodeList;
  onClose: () => void;
  onSaveComplete: () => void;
};

const REG_FIELD_LABELS: Record<number, string> = {
  [RegFieldsEnum.first_name]: 'FIRST NAME',
  [RegFieldsEnum.last_name]: 'LAST NAME',
  [RegFieldsEnum.email]: 'EMAIL',
  [RegFieldsEnum.bio]: 'BIO',
  [RegFieldsEnum.job_title]: 'JOB TITLE',
  [RegFieldsEnum.company_name]: 'COMPANY NAME',
  [RegFieldsEnum.location]: 'LOCATION',
  [RegFieldsEnum.store_name]: 'STORE NAME',
  [RegFieldsEnum.fun_fact]: 'FUN FACT',
  [RegFieldsEnum.passcode]: 'PASSCODE',
  [RegFieldsEnum.comments]: 'COMMENTS',
  [RegFieldsEnum.attend_in_person]: 'ATTEND IN PERSON',
  [RegFieldsEnum.interests]: 'INTERESTS',
  [RegFieldsEnum.state]: 'STATE',
  [RegFieldsEnum.postal_code]: 'POSTAL CODE',
  [RegFieldsEnum.phone_number]: 'PHONE NUMBER',
  [RegFieldsEnum.age]: 'AGE',
  [RegFieldsEnum.department]: 'DEPARTMENT',
  [RegFieldsEnum.industry]: 'INDUSTRY',
  [RegFieldsEnum.title]: 'TITLE',
  [RegFieldsEnum.country]: 'COUNTRY',
};

const AUTO_MAP_KEYS: Record<string, number> = {
  'first name': RegFieldsEnum.first_name,
  first_name: RegFieldsEnum.first_name,
  firstname: RegFieldsEnum.first_name,
  fname: RegFieldsEnum.first_name,
  'given name': RegFieldsEnum.first_name,
  'last name': RegFieldsEnum.last_name,
  last_name: RegFieldsEnum.last_name,
  lastname: RegFieldsEnum.last_name,
  lname: RegFieldsEnum.last_name,
  surname: RegFieldsEnum.last_name,
  'family name': RegFieldsEnum.last_name,
  email: RegFieldsEnum.email,
  'email address': RegFieldsEnum.email,
  email_address: RegFieldsEnum.email,
  bio: RegFieldsEnum.bio,
  biography: RegFieldsEnum.bio,
  'job title': RegFieldsEnum.job_title,
  job_title: RegFieldsEnum.job_title,
  jobtitle: RegFieldsEnum.job_title,
  company: RegFieldsEnum.company_name,
  'company name': RegFieldsEnum.company_name,
  company_name: RegFieldsEnum.company_name,
  organization: RegFieldsEnum.company_name,
  organisation: RegFieldsEnum.company_name,
  location: RegFieldsEnum.location,
  store: RegFieldsEnum.store_name,
  'store name': RegFieldsEnum.store_name,
  store_name: RegFieldsEnum.store_name,
  'fun fact': RegFieldsEnum.fun_fact,
  fun_fact: RegFieldsEnum.fun_fact,
  passcode: RegFieldsEnum.passcode,
  password: RegFieldsEnum.passcode,
  comments: RegFieldsEnum.comments,
  comment: RegFieldsEnum.comments,
  notes: RegFieldsEnum.comments,
  'attend in person': RegFieldsEnum.attend_in_person,
  attend_in_person: RegFieldsEnum.attend_in_person,
  interests: RegFieldsEnum.interests,
  interest: RegFieldsEnum.interests,
  state: RegFieldsEnum.state,
  province: RegFieldsEnum.state,
  'postal code': RegFieldsEnum.postal_code,
  postal_code: RegFieldsEnum.postal_code,
  zip: RegFieldsEnum.postal_code,
  'zip code': RegFieldsEnum.postal_code,
  postcode: RegFieldsEnum.postal_code,
  zipcode: RegFieldsEnum.postal_code,
  phone: RegFieldsEnum.phone_number,
  'phone number': RegFieldsEnum.phone_number,
  phone_number: RegFieldsEnum.phone_number,
  mobile: RegFieldsEnum.phone_number,
  telephone: RegFieldsEnum.phone_number,
  age: RegFieldsEnum.age,
  department: RegFieldsEnum.department,
  dept: RegFieldsEnum.department,
  industry: RegFieldsEnum.industry,
  title: RegFieldsEnum.title,
  country: RegFieldsEnum.country,
};

const REG_FIELD_IDS = new Set(Object.values(RegFieldsEnum).filter((v): v is number => typeof v === 'number'));

function autoMap(csvHeader: string, customQuestions: RegistrationQuestion[]): number | null {
  const normalized = csvHeader.toLowerCase().trim();
  if (AUTO_MAP_KEYS[normalized] !== undefined) {
    return AUTO_MAP_KEYS[normalized];
  }
  const customMatch = customQuestions.find((q) => q.name.toLowerCase().trim() === normalized);
  return customMatch?.registration_question ?? null;
}

const CsvColumnMapperModal = ({
  open,
  csvHeaders,
  workingPasscodeList,
  onClose,
  onSaveComplete,
}: CsvColumnMapperModalProps) => {
  const channel = useAppSelector(getActiveChannel);
  const projectUuid = useProjectUuidParam();
  const registrationFields = useAppSelector(selectWorkingProjectRegistrationQuestions);

  const customQuestions = useMemo(
    () => (registrationFields ?? []).filter((q) => !REG_FIELD_IDS.has(q.registration_question) && !q.deleted),
    [registrationFields]
  );

  const allFieldOptions = useMemo<FieldOption[]>(
    () => [
      ...Object.entries(REG_FIELD_LABELS).map(([id, label]) => ({
        value: Number(id),
        label,
      })),
      ...customQuestions.map((q) => ({
        value: q.registration_question,
        label: q.name.toUpperCase(),
      })),
    ],
    [customQuestions]
  );

  const fieldLabelMap = useMemo(() => new Map(allFieldOptions.map((o) => [o.value, o.label])), [allFieldOptions]);

  const [rows, setRows] = useState<MappingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoMappedCount, setAutoMappedCount] = useState(0);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setRows([]);
      setAutoMappedCount(0);
      return;
    }
    const newRows = csvHeaders.map((header) => ({
      csvHeader: header,
      enabled: true,
      fieldId: autoMap(header, customQuestions),
    }));
    setRows(newRows);
    setAutoMappedCount(newRows.filter((r) => r.fieldId !== null).length);
  }, [open, csvHeaders, customQuestions]);

  const selectedFieldIds = useMemo(
    () => new Set(rows.filter((r) => r.enabled && r.fieldId !== null).map((r) => r.fieldId)),
    [rows]
  );

  const isEmailMapped = useMemo(() => rows.some((r) => r.enabled && r.fieldId === RegFieldsEnum.email), [rows]);

  const getOptionsForRow = (rowFieldId: number | null): FieldOption[] => {
    const available = allFieldOptions.filter((opt) => !selectedFieldIds.has(opt.value) || opt.value === rowFieldId);
    if (rowFieldId === null) return available;
    const selected = available.filter((opt) => opt.value === rowFieldId);
    const rest = available.filter((opt) => opt.value !== rowFieldId);
    return [...selected, ...rest];
  };

  const handleToggle = (index: number) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const nowEnabled = !row.enabled;
        return { ...row, enabled: nowEnabled, fieldId: nowEnabled ? row.fieldId : null };
      })
    );
  };

  const handleFieldChange = (index: number, fieldId: number) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, fieldId } : row)));
  };

  const handleSave = async () => {
    if (!channel || !projectUuid) return;

    const columnMapping: CsvColumnMapping[] = rows
      .filter((r) => r.enabled && r.fieldId !== null)
      .map((r) => ({ csvHeader: r.csvHeader, fieldId: r.fieldId! }));

    setLoading(true);
    try {
      await UploadList({ channel, projectUuid, body: { workingPasscodeList, columnMapping } });
      onSaveComplete();
    } catch {
      setLoading(false);
      toastTrigger('Failed to save list', {
        description: 'There was an error uploading the audience list.',
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }
  };

  const getMappedText = () => {
    let text = 'Select which columns to import and map them to registration fields.';
    if (autoMappedCount === rows.length) {
      text = "We've automatically mapped the columns";
    } else if (autoMappedCount) {
      text = `${autoMappedCount} of ${rows.length} column${rows.length === 1 ? '' : 's'} were automatically mapped.`;
    }

    return text;
  };

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      focusLockProps={{ autoFocus: false }}
      customSize={{
        width: '620px',
        minHeight: '300px',
        height: 'calc(100% - 200px)',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      contentWrapperClassName="padding-h-40 padding-v-24"
      backgroundColor="var(--global-cont-1)"
      RenderHeaderContent={() => (
        <Container className="flex-column gap-8 padding-b-16">
          <Text className="H4" design="GlobalIcoText1">
            Map columns
          </Text>
          <Text className="P1 flex ai-center gap-4" design="GlobalIcoText3">
            <Icon name={ICONS.SPARKLE_AI} size={16} color="var(--global-ico-text-3)"></Icon>
            {getMappedText()}
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16">
          <Button
            onClick={onClose}
            backgroundColor="var(--local-bttn-sec-default-bg)"
            hoverBackgroundColor="var(--local-bttn-sec-hover-bg)"
            className="L1 padding-v-16 padding-h-24"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="padding-v-16 padding-h-24"
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            disabled={loading || !isEmailMapped}
            disabledOpacity={0.6}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              {loading ? 'Saving...' : 'Save list'}
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="flex-column padding-t-12 padding-b-16">
        <Container className="flex fd-row padding-t-8 padding-b-16">
          <Text className="H6" design="GlobalIcoText3" width="300px">
            Your file
          </Text>
          <Text className="H6" design="GlobalIcoText3">
            Available Columns
          </Text>
        </Container>

        <Container className="flex fd-column gap-8">
          {rows.map((row, index) => {
            const selectedLabel =
              row.fieldId !== null ? (fieldLabelMap.get(row.fieldId) ?? 'Select a field') : 'Select a field';

            return (
              <Container key={row.csvHeader} className="flex fd-row ai-center gap-20" height="44px">
                <Container
                  className="flex fd-row ai-center gap-12 radius-8"
                  background="var(--global-cont-2)"
                  hoverBackground="var(--global-cont-3)"
                  height="44px"
                  width="244px"
                >
                  <Checkbox
                    checked={row.enabled}
                    onChange={() => handleToggle(index)}
                    value={index}
                    className="padding-v-14 padding-h-16"
                    selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                    unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                  />
                  <Text className="P2" color={row.enabled ? 'var(--gray-100)' : 'var(--gray-500)'}>
                    {row.csvHeader}
                  </Text>
                </Container>

                <Icon name={ICONS.ARROW_RIGHT} size={16} color="var(--global-ico-text-3)" />

                <Container
                  width="244px"
                  style={{
                    opacity: row.enabled ? 1 : 0.4,
                    pointerEvents: row.enabled ? 'auto' : 'none',
                  }}
                >
                  <Popover
                    id={`csv-col-mapper-${index}`}
                    preferredPosition="bottom-left"
                    width="244px"
                    renderTriggerButton={({ open: popoverOpen, closing }) => (
                      <Container
                        className="flex ai-center jc-space-between gap-8 padding-h-12 radius-8"
                        height="44px"
                        width="244px"
                        background="var(--global-cont-2)"
                        hoverBackground="var(--global-cont-3)"
                        style={{ cursor: 'pointer' }}
                      >
                        <Text
                          className="P2"
                          design={row.fieldId !== null ? 'GlobalIcoText1' : 'GlobalIcoText3'}
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {selectedLabel}
                        </Text>
                        <RotatableIcon
                          name={ICONS.KEYBOARD_ARROW_DOWN}
                          size={12}
                          color="var(--global-ico-text-3)"
                          rotate={popoverOpen && !closing ? '180deg' : '0'}
                        />
                      </Container>
                    )}
                  >
                    {({ handleClose }) => (
                      <Container className="flex fd-column" maxHeight="200px">
                        {getOptionsForRow(row.fieldId).map((option) => (
                          <Button
                            key={option.value}
                            className="no-style flex ai-center padding-h-16 padding-v-8"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              handleFieldChange(index, option.value);
                              handleClose();
                            }}
                          >
                            <Container className="flex ai-center jc-space-between" width="100%">
                              <Text className="P2" design="GlobalIcoText1" clamp={1}>
                                {option.label}
                              </Text>
                              <Activity mode={row.fieldId === option.value ? 'visible' : 'hidden'}>
                                <Container
                                  className="flex jc-flex-end"
                                  style={{ cursor: 'pointer' }}
                                  minWidth="50px"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, fieldId: null } : r)));
                                    handleClose();
                                  }}
                                >
                                  <Text clamp={1} className="P2" color="var(--global-error-400)">
                                    Remove
                                  </Text>
                                </Container>
                              </Activity>
                            </Container>
                          </Button>
                        ))}
                      </Container>
                    )}
                  </Popover>
                </Container>
              </Container>
            );
          })}
        </Container>
      </Container>
    </Modal>
  );
};

export default CsvColumnMapperModal;
